/**
 * ScheduleHeaderSlot — owns the cross-dependent header of the Schedule
 * tab: site/bay/direction/reservation/date/start/end, bay-note banner,
 * out-of-hours warning, and the site-hours info row.
 *
 * Extracted verbatim from ScheduleTab.jsx (preserves every data-testid,
 * every behaviour: reservation-type → end_time auto-calc, bay reset
 * when site changes, requires_approval amber hint).
 */
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BOOKING_DIRECTIONS } from "@/lib/constants";
import { siteTimeBadge } from "@/lib/datetime";
import { Field } from "../_shared";
import { useSlotLabel } from "../hooks/useSlotLabels";
import { AuDateInput, AuTimeInput, minuteStepFor } from "@/components/AuDateTime";

export default function ScheduleHeaderSlot({ values, set, ctx }) {
  const { sites = [], bays = [], selectedSite, selectedBay, reservationTypes = [] } = ctx;
  const lbl = useSlotLabel("schedule.header");

  const onTypeChange = (typeId) => {
    set("reservation_type_id", typeId);
    const t = reservationTypes.find((r) => r.id === typeId);
    if (t?.default_duration_minutes && values.start_time) {
      const [h, m] = values.start_time.split(":").map(Number);
      const total = h * 60 + m + Number(t.default_duration_minutes);
      const eh = Math.floor(total / 60) % 24;
      const em = total % 60;
      const pad = (n) => String(n).padStart(2, "0");
      set("end_time", `${pad(eh)}:${pad(em)}`);
    }
  };

  const so = (selectedBay?.operating_hours_start || selectedSite?.operating_hours_start)?.slice(0, 5);
  const sc = (selectedBay?.operating_hours_end   || selectedSite?.operating_hours_end)?.slice(0, 5);
  const outOfHoursSource = selectedBay?.operating_hours_start
    ? `bay "${selectedBay?.name}"`
    : `site "${selectedSite?.name}"`;
  const outOfHours =
    selectedSite && so && sc &&
    ((values.start_time && (values.start_time < so || values.start_time > sc)) ||
     (values.end_time   && (values.end_time   < so || values.end_time   > sc)));

  return (
    <div className="space-y-3">
      {selectedBay?.booking_note && (
        <Alert data-testid="bay-booking-note" className="border-amber-300 bg-amber-50 text-amber-900">
          <AlertDescription className="whitespace-pre-wrap text-sm">
            <strong className="font-semibold">Bay note · {selectedBay.name}:</strong>{" "}
            {selectedBay.booking_note}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={lbl("site", "Site")} required>
          <Select value={values.site_id} onValueChange={(v) => { set("site_id", v); set("bay_id", ""); }}>
            <SelectTrigger data-testid="booking-site-select"><SelectValue placeholder="Pick a site" /></SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} {s.code ? `· ${s.code}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={lbl("bay", "Bay")} required hint="Bays inherit hours and slot duration from the site unless overridden. A bay flagged 'requires approval' will hold the booking as Pending until a site admin accepts it.">
          <Select value={values.bay_id} onValueChange={(v) => set("bay_id", v)} disabled={!values.site_id}>
            <SelectTrigger data-testid="booking-bay-select">
              <SelectValue placeholder={values.site_id ? "Pick a bay" : "Pick a site first"} />
            </SelectTrigger>
            <SelectContent>
              {bays.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: b.color }} />
                    {b.name} {b.code ? `· ${b.code}` : ""}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedBay?.requires_approval && (
            <p className="text-xs text-amber-700 mt-1">
              This bay requires manager approval before drivers see it.
            </p>
          )}
        </Field>

        <Field label={lbl("direction", "Direction")} required hint="Inbound = goods arriving to store; Outbound = goods leaving; Transfer = inter-site move. Determines which QA and pallet flow prompts appear on the checklist.">
          <Select value={values.direction} onValueChange={(v) => set("direction", v)}>
            <SelectTrigger data-testid="booking-direction-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BOOKING_DIRECTIONS.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {reservationTypes.length > 0 && (
          <Field label={lbl("reservation", "Reservation type")} hint="Optional. Picking a type auto-fills the end time using its default duration and colour-codes the slot on the dock grid.">
            <Select value={values.reservation_type_id || ""} onValueChange={onTypeChange}>
              <SelectTrigger data-testid="booking-type-select">
                <SelectValue placeholder="(optional) pick a type" />
              </SelectTrigger>
              <SelectContent>
                {reservationTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.name}
                      {t.default_duration_minutes ? ` · ${t.default_duration_minutes} min` : ""}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}

        <Field label={lbl("date", "Date")} required>
          <AuDateInput data-testid="booking-date-input" required
                 value={values.booking_date}
                 onChange={(e) => set("booking_date", e.target.value)} />
          {values._booking_tz && (
            <div className="text-[11px] text-slate-500 mt-1" data-testid="schedule-tz-badge">
              {siteTimeBadge(values._booking_tz)}
            </div>
          )}
        </Field>

        <Field label={lbl("start_time", "Start time")} required hint="Times outside site or bay operating hours will flash a warning and may be rejected at the gate. Reservation types can auto-set the end time.">
          <AuTimeInput data-testid="booking-start-input" minuteStep={minuteStepFor(selectedSite, selectedBay)}
                 value={values.start_time} min={so} max={sc}
                 onChange={(e) => set("start_time", e.target.value)} />
        </Field>

        <Field label={lbl("end_time", "End time")} required>
          <AuTimeInput data-testid="booking-end-input" minuteStep={minuteStepFor(selectedSite, selectedBay)}
                 value={values.end_time} min={so} max={sc}
                 onChange={(e) => set("end_time", e.target.value)} />
        </Field>
      </div>

      {outOfHours && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5"
             data-testid="booking-time-warning">
          ⚠ Time is outside {outOfHoursSource} open hours ({so} – {sc}). Bookings outside hours
          will be flagged and may be rejected at gate check.
        </div>
      )}

      {selectedSite && (
        <div className="text-xs text-slate-500 flex flex-wrap gap-3">
          <span>Site hours: {selectedSite.operating_hours_start?.slice(0,5)} – {selectedSite.operating_hours_end?.slice(0,5)}</span>
          <span>
            · Slot duration:{" "}
            {selectedBay?.slot_duration_minutes ?? selectedSite.slot_duration_minutes} min
            {selectedBay?.slot_duration_minutes && (
              <span className="text-amber-700"> (bay override)</span>
            )}
          </span>
          <span>· Timezone: {selectedSite.timezone}</span>
        </div>
      )}
    </div>
  );
}
